"use client";

import { Session, User } from "next-auth";
import { Context, createContext, useContext, useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import { MOBILE_NOT_SUPPORTED_ROUTES } from "../utils";
import { t } from "@/lib/localization";

export interface AuthContextValue {
    user: User | null;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(
        AuthContext as Context<AuthContextValue | undefined>
    );
    if (!context) {
        throw new Error("useAuth must be used within a AuthContext");
    }
    return context;
};

export interface AuthProviderProps {
    children: React.ReactNode;
    session: Session | null;
    isMobile: boolean;
}

export const AuthProvider: React.FunctionComponent<AuthProviderProps> = ({
    children,
    session,
    isMobile,
}) => {
    const pathname = usePathname();
    const router = useRouter();

    useEffect(() => {
        if (
            isMobile &&
            MOBILE_NOT_SUPPORTED_ROUTES.some((route) =>
                pathname.includes(route)
            )
        ) {
            router.replace("/mobile-not-supported");
        }
    }, [isMobile, pathname, session?.user, router]);

    return (
        <AuthContext.Provider value={{ user: session?.user || null }}>
            {children}
        </AuthContext.Provider>
    );
};
